
import React, { useState, useEffect } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Link } from 'react-router-dom';
import { useSearchParams } from "react-router-dom";
import { SERVER_HOST } from '../settings';
import { countOfGeoRankPointsAwardedPlayers } from './util/geoRankPoints';
import { displayGameMode, displayRankingType } from './util/displayNames';
import { Tooltip } from 'primereact/tooltip';

export default function Ranking({gameMode, rankingType}) {
    const [customers, setCustomers] = useState(null);
    const [filters, setFilters] = useState({
        countryName: { value: null, matchMode: FilterMatchMode.STARTS_WITH }
    });
    
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 25,
        page: 1,
        sortField: searchParams.get('sortField') || 'countryPosition',
        sortOrder: searchParams.get('sortOrder') || '1',
        filters: {
            countryName: { value: searchParams.get("countryName") || "", matchMode: 'contains' },
        }
    });

    useEffect(() => {
        const newCountryName = lazyState?.filters?.countryName?.value;
        const newSortField = lazyState?.sortField;
        const newSortOrder = lazyState?.sortOrder;
        const params = {};
        if (newCountryName) {
            params.countryName = newCountryName;
        }
        if (gameMode) {
            params.gameMode = gameMode;
        }
        if (rankingType) {
            params.rankingType = rankingType;
        }
        if (newSortField) {
            params.sortField = newSortField;
        }
        if (newSortOrder) {
            params.sortOrder = newSortOrder;
        }
        setSearchParams(params);
      }, [lazyState, gameMode, rankingType]);

    useEffect(() => {
        loadLazyData();
    }, [lazyState, rankingType]);

    const loadLazyData = () => {
        setLoading(true);
        setCustomers([])

        const queryParams = {
            ...lazyState,
            filters: JSON.stringify(lazyState.filters),
            ratingType: gameMode,
            rankingType
        };
        const encodeQueryParams = (queryParams) => {
            return Object.entries(queryParams).map(([key, value]) => `${key}=${value}`).join('&');
        };
        fetch(`${SERVER_HOST}/countriesranking?${encodeQueryParams(queryParams)}`).then(data => data.json()).then(data => {
            // console.log(data);
            setTotalRecords(data.totalRecords);
            setCustomers(data.records);
            setLoading(false);
        })

    };

    const onPage = (event) => {
        setLazyState(event);
    };

    const onSort = (event) => {
        setLazyState(event);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyState(event);
    };

    const countryBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <Link to={`/players?countryName=${rowData.countryName}`}>
                    <img src={`./flags/${rowData.countryCode}.png`} style={{ width: '24px' }} />
                    <img alt="flag" src="https://primefaces.org/cdn/primereact/images/flag/flag_placeholder.png" className={`flag flag-${rowData.countryCode}`} style={{ width: '24px' }} />
                    <span>{rowData.countryName}</span>
                </Link>
            </div>
        );
    };

    const playerBodyTemplate = (rowData) => {
        const player = rowData.nick;

        return (
            <Link to={`player/${rowData.userId}`} className="flex align-items-center gap-2">
                <div className="avatar-wrapper">
                    <div className="avatar">
                        <img className="avatar-img" src={`https://www.geoguessr.com/images/resize:auto:144:144/gravity:ce/plain/pin/${rowData.fullBodyPath}.png`}/>
                    </div>
                </div>
                <span>{player}</span>
            </Link>
        );
    };

    const numberRowFilterTemplate = (options) => {
        const [from, to] = options.value ?? [null, null];        

        return (
            <div className="flex gap-1">
                <InputNumber value={from} onChange={(e) => {console.log('filter', e); options.filterApplyCallback([e.value, to]);}} className="w-full" placeholder="from" />
                <InputNumber value={to} onChange={(e) => options.filterApplyCallback([from, e.value])} className="w-full" placeholder="to" />
            </div>
        );
    };

    const geoPointsHeader = (
        <span>
            <span>GeoRank Points</span>
            &nbsp;&nbsp;
            <span>
                <Tooltip target={`.georank-points-info`} mouseTrack mouseTrackLeft={10}/>
                <i className='pi pi-info-circle georank-points-info' data-pr-tooltip={`${countOfGeoRankPointsAwardedPlayers(rankingType, gameMode)} players awarded 'GeoRank points' in ${displayRankingType(rankingType)} ranking in ${displayGameMode(gameMode)} game mode`}></i>
            </span>
        </span>
    )

    return (
        <div className="grid">
            <div className="card col-12 xl:col-10 xl:col-offset-1">
                <DataTable value={customers} stripedRows paginator rows={lazyState.rows} rowsPerPageOptions={[10, 25, 50]} dataKey="id" filterDisplay="row" loading={loading}
                        globalFilterFields={['nick', 'countryName']} emptyMessage="No customers found." lazy totalRecords={totalRecords} onPage={onPage}
                        first={lazyState.first} onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
                        onFilter={onFilter} filters={lazyState.filters} >
                    <Column field="countryPosition" header="Pos." sortable style={{ minWidth: '1rem' }} />
                    <Column header="Country" filterField="countryName" style={{ minWidth: '12rem' }} showFilterMenu={false} body={countryBodyTemplate} filter filterPlaceholder="Search by country" />
                    <Column field="rankedPlayersPerCountry" header="Number of points-scoring players" sortable style={{ minWidth: '1rem', 'textAlign': 'center' }} showFilterMenu={false} filterElement={numberRowFilterTemplate} />
                    <Column field="totalPlayersPerCountry" header="Number of ranked players" sortable style={{ minWidth: '1rem', 'textAlign': 'center' }} showFilterMenu={false} filterElement={numberRowFilterTemplate} />
                    <Column field="totalPoints" header={geoPointsHeader} sortable style={{ minWidth: '1rem', 'textAlign': 'center' }} showFilterMenu={false} filterElement={numberRowFilterTemplate} />
                </DataTable>
            </div>
        </div>
    );
}
        